import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import MeetingCard from '../components/MeetingCard';
import Spinner from '../components/Spinner';
import { getMyDashboardMetrics, listMyMeetings } from '../lib/api';
import { errorMessage, getMalaysiaTodayISO } from '../lib/utils';
import type { DashboardMetrics, MeetingCardData } from '../types';
import { useStaffAuth } from '../auth/StaffAuthContext';
import { useToast } from '../context/ToastContext';
import MeetingCodeForm from '../components/MeetingCodeForm';

const emptyMetrics: DashboardMetrics = { scheduled_meetings: 0, total_hosted: 0, total_checked_in: 0 };
type DashboardStatusFilter = 'all' | 'HOST' | 'COHOST' | 'EXPECTED' | 'ATTENDED' | 'cancelled';

function defaultDashboardDateRange() {
  const today = getMalaysiaTodayISO();
  const [year, month] = today.split('-').map(Number);
  const lastDayOfNextMonth = new Date(Date.UTC(year, month + 1, 0));
  const toIso = (date: Date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
  return { from: `${year}-${String(month).padStart(2, '0')}-01`, to: toIso(lastDayOfNextMonth) };
}

export default function DashboardPage() {
  const { identity } = useStaffAuth();
  const [metrics, setMetrics] = useState(emptyMetrics);
  const [meetings, setMeetings] = useState<MeetingCardData[]>([]);
  const [search, setSearch] = useState('');
  const initialDateRange = useMemo(defaultDashboardDateRange, []);
  const [dateFrom, setDateFrom] = useState(initialDateRange.from);
  const [dateTo, setDateTo] = useState(initialDateRange.to);
  const [statusFilter, setStatusFilter] = useState<DashboardStatusFilter>('all');
  const [busy, setBusy] = useState(true);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const load = useCallback(async (silent = false) => {
    if (!silent) setBusy(true);
    try {
      const [nextMetrics, nextMeetings] = await Promise.all([
        getMyDashboardMetrics(),
        listMyMeetings(),
      ]);
      setMetrics(nextMetrics);
      setMeetings(nextMeetings);
    } catch (error) {
      showToast(errorMessage(error), 'error');
    } finally {
      if (!silent) setBusy(false);
    }
  }, [showToast]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const poll = window.setInterval(() => { if (!document.hidden) void load(true); }, 30000);
    return () => window.clearInterval(poll);
  }, [load]);

  const resetFilters = () => {
    const defaults = defaultDashboardDateRange();
    setSearch('');
    setDateFrom(defaults.from);
    setDateTo(defaults.to);
    setStatusFilter('all');
    void load();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rangeIsValid = !dateFrom || !dateTo || dateFrom <= dateTo;
    if (!rangeIsValid) return [];
    return meetings.filter((m) => {
      const matchesDate = (!dateFrom || m.meeting_date >= dateFrom) && (!dateTo || m.meeting_date <= dateTo);
      const matchesSearch = !q
        || m.title.toLowerCase().includes(q)
        || m.host_email.toLowerCase().includes(q)
        || (m.location ?? '').toLowerCase().includes(q)
        || (m.chairman ?? '').toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'cancelled' ? m.status === 'Cancelled' : m.relationship === statusFilter);
      return matchesDate && matchesSearch && matchesStatus;
    });
  }, [dateFrom, dateTo, meetings, search, statusFilter]);

  const dateRangeIsValid = !dateFrom || !dateTo || dateFrom <= dateTo;

  const { upcoming, past, nearestActiveUpcomingId } = useMemo(() => {
    const today = getMalaysiaTodayISO();
    const byUpcoming = (a: MeetingCardData, b: MeetingCardData) => a.meeting_date.localeCompare(b.meeting_date) || a.meeting_time.localeCompare(b.meeting_time);
    const byPast = (a: MeetingCardData, b: MeetingCardData) => b.meeting_date.localeCompare(a.meeting_date) || b.meeting_time.localeCompare(a.meeting_time);
    const upcomingRows = filtered.filter((meeting) => meeting.meeting_date >= today).sort(byUpcoming);
    const pastRows = filtered.filter((meeting) => meeting.meeting_date < today).sort(byPast);
    return {
      upcoming: upcomingRows,
      past: pastRows,
      nearestActiveUpcomingId: upcomingRows.find((meeting) => meeting.status === 'Active')?.meeting_id,
    };
  }, [filtered]);

  return (
    <Layout>
      <div className="dashboard-title-row">
        <div><h1>{identity.full_name}</h1><p className="muted">Selamat kembali ke Kehadiran MTIB.</p></div>
        <div className="dashboard-primary-actions">
          <MeetingCodeForm compact buttonLabel="Sertai" onResolved={(meetingId) => navigate(`/checkin/${meetingId}`)} />
          <button className="button button-primary" onClick={() => navigate('/meetings/new')}>+ Mesyuarat</button>
        </div>
      </div>

      <section className="metric-grid" aria-label="Ringkasan kehadiran anda">
        <article className="metric-card"><span>Mesyuarat Dijadualkan</span><strong>{metrics.scheduled_meetings}</strong><small>Mesyuarat akan datang yang berkaitan dengan anda</small></article>
        <article className="metric-card"><span>Mesyuarat Dianjurkan</span><strong>{metrics.total_hosted}</strong><small>Mesyuarat yang anda anjurkan tahun ini</small></article>
        <article className="metric-card"><span>Rekod Kehadiran</span><strong>{metrics.total_checked_in}</strong><small>Rekod kehadiran anda tahun ini</small></article>
      </section>

      <section className="section-block">
        <div className="section-toolbar dashboard-meetings-toolbar">
          <div>
            <h2>Mesyuarat Anda</h2>
            <p className="muted">Mesyuarat yang anda anjurkan, dijemput hadir, atau telah hadiri dipaparkan di sini.</p>
          </div>
          <div className="dashboard-filter-toolbar" aria-label="Tapisan mesyuarat">
            <input className="search-input" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari mesyuarat / lokasi…" aria-label="Cari mesyuarat atau lokasi" />
            <label className="filter-field"><span>Tarikh dari</span><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></label>
            <label className="filter-field"><span>Hingga</span><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></label>
            <label className="filter-field"><span>Status</span><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as DashboardStatusFilter)}>
              <option value="all">Semua</option>
              <option value="HOST">Host</option>
              <option value="COHOST">Co-Host</option>
              <option value="EXPECTED">Dijemput</option>
              <option value="ATTENDED">Telah Hadir</option>
              <option value="cancelled">Dibatalkan</option>
            </select></label>
            <button type="button" className="button button-secondary dashboard-reset-button" onClick={resetFilters}>Reset</button>
          </div>
        </div>
        {!dateRangeIsValid ? <div className="empty-state compact"><p>Tarikh mula mesti pada atau sebelum tarikh akhir.</p></div> : busy ? <Spinner label="Sedang memuatkan mesyuarat anda…" /> : filtered.length ? <div className="meeting-section-list">
          <p className="dashboard-result-count" role="status">{filtered.length} {filtered.length === 1 ? 'mesyuarat' : 'mesyuarat'} sepadan dengan tapisan.</p>
          <div className="meeting-section-heading"><h2>Mesyuarat Akan Datang</h2><p className="muted">Hari ini dan tarikh akan datang, paling hampir dahulu.</p></div>
          {upcoming.length ? <div className="meeting-grid">
            {upcoming.map((meeting) => (
              <MeetingCard
                key={meeting.meeting_id}
                meeting={meeting}
                isNearestActiveUpcoming={meeting.meeting_id === nearestActiveUpcomingId}
                onOpen={() => navigate(`/meetings/${meeting.meeting_id}`)}
              />
            ))}
          </div> : <div className="empty-state compact"><p>Tiada mesyuarat akan datang yang sepadan dengan carian anda.</p></div>}
          <div className="meeting-section-heading"><h2>Mesyuarat Terdahulu</h2><p className="muted">Yang paling terkini dahulu.</p></div>
          {past.length ? <div className="meeting-grid">
            {past.map((meeting) => <MeetingCard key={meeting.meeting_id} meeting={meeting} onOpen={() => navigate(`/meetings/${meeting.meeting_id}`)} />)}
          </div> : <div className="empty-state compact"><p>Tiada mesyuarat terdahulu yang sepadan dengan carian anda.</p></div>}
        </div> : <div className="empty-state"><h3>Tiada mesyuarat sepadan</h3><p>Cuba ubah carian, julat tarikh atau status. Mesyuarat yang berkaitan dengan anda akan dipaparkan di sini.</p></div>}
      </section>
    </Layout>
  );
}
